<template>
  <Teleport to="body">
    <Transition name="toast" appear>
      <div
        v-if="visible"
        :class="[
          'toast',
          `toast--${type}`,
          position
        ]"
        role="alert"
        aria-live="assertive"
      >
        <div class="toast__icon">
          <component :is="iconComponent" />
        </div>
        <div class="toast__content">
          <div class="toast__title" v-if="title">{{ title }}</div>
          <div class="toast__message">{{ message }}</div>
        </div>
        <button 
          v-if="closable"
          @click="close"
          class="toast__close"
          aria-label="닫기"
        >
          ×
        </button>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'

const props = defineProps({
  type: {
    type: String,
    default: 'info',
    validator: (value) => ['success', 'error', 'warning', 'info'].includes(value)
  },
  title: {
    type: String,
    default: ''
  },
  message: {
    type: String,
    required: true
  },
  duration: {
    type: Number,
    default: 4000
  },
  position: {
    type: String,
    default: 'toast--top-right',
    validator: (value) => [
      'toast--top-left',
      'toast--top-right', 
      'toast--bottom-left',
      'toast--bottom-right',
      'toast--top-center'
    ].includes(value)
  },
  closable: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['close'])

const visible = ref(false)

const iconComponent = computed(() => {
  const icons = {
    success: 'CheckCircleIcon',
    error: 'XCircleIcon', 
    warning: 'ExclamationTriangleIcon',
    info: 'InformationCircleIcon'
  }
  return icons[props.type] || 'InformationCircleIcon'
})

const close = () => {
  visible.value = false
  setTimeout(() => {
    emit('close')
  }, 300)
}

onMounted(() => {
  visible.value = true
  
  if (props.duration > 0) {
    setTimeout(close, props.duration)
  }
})

// 아이콘 컴포넌트들 (간단한 SVG)
const CheckCircleIcon = {
  template: `
    <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
    </svg>
  `
}

const XCircleIcon = {
  template: `
    <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
    </svg>
  `
}

const ExclamationTriangleIcon = {
  template: `
    <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
      <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
    </svg>
  `
}

const InformationCircleIcon = {
  template: `
    <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
      <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
    </svg>
  `
}
</script>

<style scoped>
.toast {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  border-left: 4px solid;
  max-width: 420px;
  min-width: 300px;
  position: fixed;
  z-index: 9999;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* 위치 스타일 */
.toast--top-right {
  top: 20px;
  right: 20px;
}

.toast--top-left {
  top: 20px;
  left: 20px;
}

.toast--bottom-right {
  bottom: 20px;
  right: 20px;
}

.toast--bottom-left {
  bottom: 20px;
  left: 20px;
}

.toast--top-center {
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
}

/* 타입별 스타일 */
.toast--success {
  border-left-color: #10b981;
}

.toast--success .toast__icon {
  color: #10b981;
}

.toast--error {
  border-left-color: #ef4444;
}

.toast--error .toast__icon {
  color: #ef4444;
}

.toast--warning {
  border-left-color: #f59e0b;
}

.toast--warning .toast__icon {
  color: #f59e0b;
}

.toast--info {
  border-left-color: #3b82f6;
}

.toast--info .toast__icon {
  color: #3b82f6;
}

/* 내용 스타일 */
.toast__content {
  flex: 1;
}

.toast__title {
  font-weight: 600;
  font-size: 14px;
  color: #374151;
  margin-bottom: 4px;
}

.toast__message {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.4;
}

.toast__close {
  background: none;
  border: none;
  font-size: 18px;
  color: #9ca3af;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.toast__close:hover {
  color: #374151;
}

/* 애니메이션 */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

/* 왼쪽 위치의 경우 애니메이션 방향 조정 */
.toast--top-left.toast-enter-from,
.toast--bottom-left.toast-enter-from {
  transform: translateX(-100%);
}

.toast--top-left.toast-leave-to,
.toast--bottom-left.toast-leave-to {
  transform: translateX(-100%);
}

/* 상단 중앙의 경우 */
.toast--top-center.toast-enter-from,
.toast--top-center.toast-leave-to {
  transform: translateX(-50%) translateY(-100%);
}
</style>